import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../services/supabase';
import { useTriviaAdmin, useTriviaBadgeListAdmin, TriviaQuestion } from '../hooks/useTriviaAdmin';
import { useSermons } from '../hooks/useSermons';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

type EditableOption = {
  id?: string;
  option_text: string;
  is_correct: boolean;
  position: number;
};

type EditableQuestion = {
  id?: string;
  question_text: string;
  position: number;
  options: EditableOption[];
};

const QUESTION_COUNT = 10;
const OPTION_COUNT = 4;

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:focus {
    outline: 2px solid var(--primary);
    border-color: var(--primary);
  }
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  min-height: 90px;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:focus {
    outline: 2px solid var(--primary);
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:focus {
    outline: 2px solid var(--primary);
    border-color: var(--primary);
  }
`;

const QuestionGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  tbody tr:hover {
    background: rgba(255, 255, 255, 0.04);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const BadgeUser = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`;

const BadgeAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
`;

const BadgeInitials = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--outline);
  color: var(--onSurfaceVariant);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
`;

const QuestionCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
  background: var(--surface);
  color: var(--onSurface);
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 0.6rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const Radio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
`;

const SaveButton = styled.button`
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.75rem 1.2rem;
  border-radius: 12px;
  border: 1px solid var(--primaryDark);
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.75rem 1.2rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.75rem 1.2rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const HelperText = styled.p`
  margin: 0.25rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const ErrorText = styled.p`
  color: var(--error);
  font-weight: 600;
`;

const LoadingMessage = styled.p`
  color: var(--onSurface);
  padding: 1.5rem;
`;

const ErrorMessage = styled.p`
  color: var(--error);
  padding: 1.5rem;
  font-weight: 600;
`;

const CardTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const buildEmptyQuestions = (): EditableQuestion[] =>
  Array.from({ length: QUESTION_COUNT }, (_, index) => ({
    question_text: '',
    position: index + 1,
    options: Array.from({ length: OPTION_COUNT }, (__, optIndex) => ({
      option_text: '',
      is_correct: optIndex === 0,
      position: optIndex + 1,
    })),
  }));

const mergeQuestions = (existing: TriviaQuestion[]): EditableQuestion[] => {
  const base = buildEmptyQuestions();
  return base.map((defaultQuestion) => {
    const found = existing.find((q) => q.position === defaultQuestion.position);
    if (!found) return defaultQuestion;
    const mergedOptions = defaultQuestion.options.map((opt) => {
      const match = found.options.find((o) => o.position === opt.position);
      return {
        ...opt,
        id: match?.id,
        option_text: match?.option_text ?? '',
        is_correct: match?.is_correct ?? false,
      };
    });
    return {
      id: found.id,
      question_text: found.question_text ?? '',
      position: found.position,
      options: mergedOptions,
    };
  });
};

export const TriviaPage: React.FC = () => {
  const { data, isLoading, error } = useTriviaAdmin();
  const { data: sermons = [], isLoading: isLoadingSermons } = useSermons();
  const queryClient = useQueryClient();
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');
  const [selectedSermonId, setSelectedSermonId] = useState('');
  const [questions, setQuestions] = useState<EditableQuestion[]>(buildEmptyQuestions());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [forceNewQuiz, setForceNewQuiz] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const { data: badgeList = [] } = useTriviaBadgeListAdmin(
    forceNewQuiz ? null : data?.quiz?.id,
    data?.quiz?.created_at ?? null
  );

  const quiz = data?.quiz ?? null;

  useEffect(() => {
    if (!data) return;
    if (!data.quiz) {
      setQuizTitle('');
      setQuizDescription('');
      setSelectedSermonId('');
      setQuestions(buildEmptyQuestions());
      setForceNewQuiz(false);
      return;
    }
    setQuizTitle(data.quiz.title ?? '');
    setQuizDescription(data.quiz.description ?? '');
    setSelectedSermonId(data.quiz.sermon_id ?? '');
    setQuestions(mergeQuestions(data.questions ?? []));
    setForceNewQuiz(false);
  }, [data]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!quizTitle.trim()) errors.push('Quiz title is required.');
    questions.forEach((q, idx) => {
      if (!q.question_text.trim()) {
        errors.push(`Question ${idx + 1} is required.`);
      }
      const filledOptions = q.options.filter((o) => o.option_text.trim());
      if (filledOptions.length < OPTION_COUNT) {
        errors.push(`Question ${idx + 1} needs ${OPTION_COUNT} options.`);
      }
      const correctCount = q.options.filter((o) => o.is_correct).length;
      if (correctCount !== 1) {
        errors.push(`Question ${idx + 1} must have exactly one correct option.`);
      }
    });
    return errors;
  }, [questions, quizTitle]);

  const handleQuestionChange = (index: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q, idx) => (idx === index ? { ...q, question_text: value } : q))
    );
  };

  const handleOptionChange = (qIndex: number, oIndex: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q, idx) => {
        if (idx !== qIndex) return q;
        const options = q.options.map((o, optIdx) =>
          optIdx === oIndex ? { ...o, option_text: value } : o
        );
        return { ...q, options };
      })
    );
  };

  const handleCorrectSelect = (qIndex: number, oIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, idx) => {
        if (idx !== qIndex) return q;
        const options = q.options.map((o, optIdx) => ({
          ...o,
          is_correct: optIdx === oIndex,
        }));
        return { ...q, options };
      })
    );
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(null);
    if (validationErrors.length) {
      setSaveError(validationErrors[0]);
      return;
    }
    setSaving(true);
    try {
      const { error: resetError } = await supabase
        .from('trivia_quizzes')
        .update({ is_active: false })
        .neq('is_active', false);
      if (resetError) throw resetError;

      let quizId = forceNewQuiz ? undefined : quiz?.id;
      if (!quizId) {
        const { data: created, error: createError } = await supabase
          .from('trivia_quizzes')
          .insert({
            title: quizTitle.trim(),
            description: quizDescription.trim() || null,
            sermon_id: selectedSermonId || null,
            is_active: true,
          })
          .select('id')
          .single();
        if (createError) throw createError;
        quizId = created.id;
      } else {
        const { error: updateError } = await supabase
          .from('trivia_quizzes')
          .update({
            title: quizTitle.trim(),
            description: quizDescription.trim() || null,
            sermon_id: selectedSermonId || null,
            is_active: true,
          })
          .eq('id', quizId);
        if (updateError) throw updateError;
      }

      for (const q of questions) {
        let questionId = q.id;
        if (questionId) {
          const { error: updateError } = await supabase
            .from('trivia_questions')
            .update({
              question_text: q.question_text.trim(),
              position: q.position,
            })
            .eq('id', questionId);
          if (updateError) throw updateError;
        } else {
          const { data: created, error: insertError } = await supabase
            .from('trivia_questions')
            .insert({
              quiz_id: quizId,
              question_text: q.question_text.trim(),
              position: q.position,
            })
            .select('id')
            .single();
          if (insertError) throw insertError;
          questionId = created.id;
        }

        const { error: deleteError } = await supabase
          .from('trivia_options')
          .delete()
          .eq('question_id', questionId);
        if (deleteError) throw deleteError;

        const { error: optionsError } = await supabase.from('trivia_options').insert(
          q.options.map((opt) => ({
            question_id: questionId,
            option_text: opt.option_text.trim(),
            is_correct: opt.is_correct,
            position: opt.position,
          }))
        );
        if (optionsError) throw optionsError;
      }

      setSaveSuccess('Trivia quiz saved and set as active.');
      setForceNewQuiz(false);
      queryClient.invalidateQueries({ queryKey: ['trivia', 'admin', 'active'] });
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save trivia quiz.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewQuiz = () => {
    setQuizTitle('');
    setQuizDescription('');
    setSelectedSermonId('');
    setQuestions(buildEmptyQuestions());
    setSaveError(null);
    setSaveSuccess(null);
    setJsonError(null);
    setForceNewQuiz(true);
  };

  const handleJsonUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setJsonError(null);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as {
        title?: string;
        description?: string;
        sermon_id?: string;
        questions?: Array<{
          position?: number;
          question_text?: string;
          options?: Array<{
            position?: number;
            option_text?: string;
            is_correct?: boolean;
          }>;
        }>;
      };

      if (!parsed || !Array.isArray(parsed.questions)) {
        throw new Error('JSON must include a "questions" array.');
      }
      if (parsed.questions.length !== QUESTION_COUNT) {
        throw new Error(`JSON must include exactly ${QUESTION_COUNT} questions.`);
      }

      const nextQuestions: EditableQuestion[] = parsed.questions.map((q, qIndex) => {
        if (!Array.isArray(q.options)) {
          throw new Error(`Question ${qIndex + 1} must include an "options" array.`);
        }
        if (q.options.length !== OPTION_COUNT) {
          throw new Error(`Question ${qIndex + 1} must include ${OPTION_COUNT} options.`);
        }
        const correctCount = q.options.filter((opt) => opt.is_correct).length;
        if (correctCount !== 1) {
          throw new Error(`Question ${qIndex + 1} must have exactly one correct option.`);
        }
        return {
          question_text: q.question_text?.toString() ?? '',
          position: q.position ?? qIndex + 1,
          options: q.options.map((opt, optIndex) => ({
            option_text: opt.option_text?.toString() ?? '',
            is_correct: Boolean(opt.is_correct),
            position: opt.position ?? optIndex + 1,
          })),
        };
      });

      setQuizTitle(parsed.title?.toString() ?? '');
      setQuizDescription(parsed.description?.toString() ?? '');
      setSelectedSermonId(
        typeof parsed.sermon_id === 'string' && parsed.sermon_id.trim() ? parsed.sermon_id.trim() : '',
      );
      setQuestions(nextQuestions);
      setForceNewQuiz(true);
    } catch (err: any) {
      setJsonError(err.message || 'Failed to parse JSON.');
    } finally {
      event.target.value = '';
    }
  };

  if (isLoading) return <LoadingMessage>Loading trivia quiz…</LoadingMessage>;
  if (error) return <ErrorMessage>Failed to load trivia quiz.</ErrorMessage>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Trivia Quiz</HeaderTitle>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <label>
            <SecondaryButton as="span">Upload JSON</SecondaryButton>
            <input
              type="file"
              accept="application/json"
              onChange={handleJsonUpload}
              style={{ display: 'none' }}
            />
          </label>
          <SecondaryLink to="/trivia/results">Past Results</SecondaryLink>
          <SecondaryButton onClick={handleNewQuiz} disabled={saving}>
            New Quiz
          </SecondaryButton>
          <SaveButton onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Trivia Quiz'}
          </SaveButton>
        </div>
      </HeaderRow>

      <Card>
        <FieldRow>
          <Label htmlFor="quiz-title">Quiz Title</Label>
          <Input
            id="quiz-title"
            value={quizTitle}
            onChange={(e) => setQuizTitle(e.target.value)}
            placeholder="Weekly Bible Trivia"
          />
        </FieldRow>
        <FieldRow>
          <Label htmlFor="quiz-description">Quiz Description</Label>
          <TextArea
            id="quiz-description"
            value={quizDescription}
            onChange={(e) => setQuizDescription(e.target.value)}
            placeholder="Short context or theme for this week's quiz."
          />
        </FieldRow>
        <FieldRow>
          <Label htmlFor="quiz-sermon">Linked Sermon</Label>
          <Select
            id="quiz-sermon"
            value={selectedSermonId}
            onChange={(e) => setSelectedSermonId(e.target.value)}
            disabled={isLoadingSermons}
          >
            <option value="">No linked sermon</option>
            {sermons.map((sermon) => (
              <option key={sermon.id} value={sermon.id}>
                {sermon.title}
                {sermon.date ? ` (${sermon.date})` : ''}
              </option>
            ))}
          </Select>
          <HelperText>
            The linked sermon will show the Take Quiz button in the mobile app and on the website.
          </HelperText>
        </FieldRow>
        <HelperText>
          Badge thresholds (10 questions): 0–3 Bronze, 4–7 Silver, 8–10 Gold.
        </HelperText>
        {forceNewQuiz ? (
          <HelperText>New quiz mode: badge list will reset for this quiz.</HelperText>
        ) : null}
        {jsonError ? <ErrorText>{jsonError}</ErrorText> : null}
        {saveError ? <ErrorText>{saveError}</ErrorText> : null}
        {saveSuccess ? <HelperText>{saveSuccess}</HelperText> : null}
      </Card>

      <QuestionGrid>
        {questions.map((question, qIndex) => (
          <QuestionCard key={`question-${question.position}`}>
            <QuestionHeader>
              <strong>Question {question.position}</strong>
            </QuestionHeader>
            <FieldRow>
              <Input
                value={question.question_text}
                onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                placeholder="Enter the question text"
              />
            </FieldRow>
            {question.options.map((option, oIndex) => (
              <OptionRow key={`question-${question.position}-opt-${option.position}`}>
                <Radio
                  type="radio"
                  name={`question-${question.position}-correct`}
                  checked={option.is_correct}
                  onChange={() => handleCorrectSelect(qIndex, oIndex)}
                />
                <Input
                  value={option.option_text}
                  onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                  placeholder={`Option ${option.position}`}
                />
              </OptionRow>
            ))}
          </QuestionCard>
        ))}
      </QuestionGrid>

      <Card>
        <CardTitle>Badge List</CardTitle>
        {badgeList.length === 0 ? (
          <HelperText>No shared scores yet.</HelperText>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>User</th>
                <th>Badge</th>
                <th>Score</th>
                <th>Change vs last quiz</th>
              </tr>
            </thead>
            <tbody>
              {badgeList.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <BadgeUser>
                      {entry.user_avatar ? (
                        <BadgeAvatar src={entry.user_avatar} alt={entry.user_name} />
                      ) : (
                        <BadgeInitials>
                          {entry.user_name.substring(0, 2).toUpperCase()}
                        </BadgeInitials>
                      )}
                      {entry.user_name}
                    </BadgeUser>
                  </td>
                  <td>{entry.badge}</td>
                  <td>{entry.score}/{QUESTION_COUNT}</td>
                  <td>
                    {entry.score_delta === null || entry.score_delta === undefined
                      ? 'New'
                      : entry.score_delta === 0
                        ? 'Same'
                        : entry.score_delta > 0
                          ? `+${entry.score_delta}`
                          : `${entry.score_delta}`}
                  </td>
                </tr>
              ))}
            </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </Page>
  );
};
